// Referrals, Introductions (docs/referrals-spec.md 4.4): the ledger. Owed right now, the filters, then every
// line, newest first: a table at laptop width and a card per line at phone width, with the same buttons. Each
// button is 1 of migration 79's staff writes; the database refuses anything a line's state doesn't allow, and
// the page reads the ledger again after each, so what shows is always the database's.
//
// ?line=<id> (Today's cards, the person page's card) picks that line out and scrolls to it.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { usePhone } from '../../lib/media.js'
import { Button, Card, Chip, Chips, ConfirmPanel, EmptyState, Field, Loading, Problem, Select, StatusPill, adminPath, useAdmin } from '../ui/index.js'
import { shortName } from '../people/words.js'
import {
  REF, STATUS_WORDS, dayMonthYear, filterLines, firstName, flagWords, gbp, lineActions, needsLookCount, owedByReferrer, refundWords, refusal,
} from './words.js'
import { AddDialog, PaidDialog, QualifyDialog, ReassignDialog } from './Dialogs.jsx'

const TONE = { applied: 'paused', joined: 'paused', qualified: 'attention', paid: 'active', not_proceeding: 'ended' }
// the referred person's email kept on 1 line where it fits (210px holds a 30-character address)
const COLS = 'minmax(210px, 1.35fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(0, 1fr) 72px 98px minmax(190px, 1.15fr)'

function Pill({ l }) {
  return <StatusPill tone={TONE[l.status] || 'paused'}>{STATUS_WORDS[l.status] || l.status}</StatusPill>
}

// The referrer, their code and Change. A phone card has its own Code row, so it leaves the code out here (the review
// of 18 Oct: the same code twice on a card)
function Referrer({ l, onChange, code = true }) {
  return (
    <span className="rf-referrer">
      <Link to={adminPath.person(l.referrer_person_id)} className="ad-td-main">{l.referrer_full_name}</Link>
      <span className="rf-referrer-bits">
        {code && l.referrer_code && <code className="rf-code">{l.referrer_code}</code>}
        {l.status !== 'paid' && (
          <button type="button" className="rf-text-btn" aria-label={REF.changeLabel(l.referred_name || REF.someone)} onClick={() => onChange(l)}>{REF.change}</button>
        )}
      </span>
    </span>
  )
}

function Actions({ l, products, busy, onAct }) {
  const acts = lineActions(l)
  const [place, setPlace] = useState('')
  if (!acts.length) return l.status === 'paid' && l.payment_reference ? <span className="rf-ref">{REF.ref(l.payment_reference)}</span> : null
  // a refunded line's first choice is Not proceeding, never paying it (the review of 18 Oct)
  const main = acts.includes('accept') ? 'accept' : l.refunded_at && acts.includes('notProceeding') ? 'notProceeding'
    : acts.includes('qualify') ? 'qualify' : acts.includes('markPaid') ? 'markPaid' : null
  return (
    <span className="rf-acts">
      {acts.includes('placeOn') && (
        <Select aria-label={REF.buttons.placeOn} className="rf-place" value={place} onChange={setPlace} placeholder={REF.buttons.placeOn}
          options={products.map(p => ({ value: p.id, label: shortName(p) }))} />
      )}
      {acts.filter(a => a !== 'placeOn').map(a => (
        <Button key={a} size="sm" kind={a === main ? 'ink' : 'line'} busy={busy === `${l.id}:${a}`} disabled={Boolean(busy)}
          onClick={() => onAct(l, a, { product: place || null })}>{REF.buttons[a]}</Button>
      ))}
    </span>
  )
}

export function Introductions({ ledger, offers, products, focusLine }) {
  const { api, setFlash } = useAdmin()
  const phone = usePhone()
  const [status, setStatus] = useState('')
  const [product, setProduct] = useState('')
  const [dialog, setDialog] = useState(null) // { kind, line }
  const [confirm, setConfirm] = useState(null) // a second claim's line id, asked before Accept
  const [busy, setBusy] = useState(null)
  const picked = useRef(null)

  const lines = ledger.data || []
  const shown = useMemo(() => filterLines(lines, { status, product }), [lines, status, product])
  const owed = useMemo(() => owedByReferrer(lines), [lines])
  const looks = needsLookCount(lines)
  const offerBy = useMemo(() => new Map((offers || []).map(o => [o.product_id, o])), [offers])
  const productById = useMemo(() => new Map(products.map(p => [p.id, p])), [products])
  const withOffers = useMemo(() => products.filter(p => offerBy.has(p.id)), [products, offerBy])

  // ?line=<id>: once the ledger is in, scroll to that line
  useEffect(() => {
    if (!focusLine || !ledger.data || picked.current === focusLine) return
    const el = document.getElementById(`rf-line-${focusLine}`)
    if (el) { picked.current = focusLine; el.scrollIntoView({ block: 'center' }) }
  }, [focusLine, ledger.data, phone])

  const done = text => { setFlash({ text }); setDialog(null); setConfirm(null); ledger.reload() }
  const failed = e => setFlash({ text: refusal(e), tone: 'problem' })

  async function onAct(l, act, { product: placeOn } = {}) {
    if (act === 'qualify') return setDialog({ kind: 'qualify', line: l })
    if (act === 'markPaid') return setDialog({ kind: 'paid', line: l })
    if (act === 'accept' && l.flag === 'second_claim' && confirm !== l.id) return setConfirm(l.id)
    setBusy(`${l.id}:${act}`)
    const first = firstName(l, 'referrer_')
    try {
      if (act === 'joined') { await api.set_introduction_status({ id: l.id, status: 'joined' }); done(REF.flash.joined) }
      else if (act === 'notProceeding') { await api.set_introduction_status({ id: l.id, status: 'not_proceeding' }); done(REF.flash.notProceeding) }
      else if (act === 'accept') { await api.decide_introduction({ id: l.id, accept: true, product: l.flag === 'no_product' ? placeOn || undefined : undefined }); done(REF.flash.accepted(first)) }
      else if (act === 'turnDown') { await api.decide_introduction({ id: l.id, accept: false }); done(REF.flash.turnedDown(first)) }
    } catch (e) {
      failed(e)
    } finally {
      setBusy(null)
    }
  }

  const productWord = l => (l.product_id ? shortName(productById.get(l.product_id) || { name: l.product_name }) : REF.noProduct)
  const amountWord = l => (l.payout_amount != null ? gbp(l.payout_amount) : REF.none)
  const confirmFor = l => (confirm === l.id
    ? <ConfirmPanel text={REF.secondClaimAsk(l.first_referrer_full_name)} confirmLabel={REF.buttons.acceptAsWell} cancelLabel={REF.buttons.keepOpen}
        busy={busy === `${l.id}:accept`} onConfirm={() => onAct(l, 'accept')} onCancel={() => setConfirm(null)} />
    : null)

  let body
  if (ledger.status === 'loading' && !ledger.data) body = <Loading />
  else if (!ledger.data) body = <Problem error={ledger.error} onRetry={ledger.reload} />
  else if (!shown.length) body = <div className="rf-box"><EmptyState>{REF.empty}</EmptyState></div>
  else if (phone) {
    body = (
      <ul className="rf-cards" aria-label={REF.tableLabel}>
        {shown.map(l => {
          const flag = flagWords(l)
          const refund = refundWords(l)
          return (
            <li key={l.id} id={`rf-line-${l.id}`} className={`rf-card${focusLine === l.id ? ' rf-card--picked' : ''}`}>
              <div className="rf-card-top">
                <span className="rf-card-who">
                  <span className="ad-td-main">{l.referred_name || REF.someone}</span>
                  {l.referred_email && <span className="ad-td-sub">{l.referred_email}</span>}
                </span>
                <span className="rf-card-pill"><Pill l={l} /></span>
              </div>
              {flag && <span className="rf-flag">{flag}</span>}
              {refund && <span className="rf-flag rf-refunded">{refund}</span>}
              <dl className="rf-card-rows">
                <div><dt>{REF.rows.referrer}</dt><dd><Referrer l={l} code={false} onChange={line => setDialog({ kind: 'reassign', line })} /></dd></div>
                <div><dt>{REF.rows.product}</dt><dd>{productWord(l)}</dd></div>
                <div><dt>{REF.rows.amount}</dt><dd>{amountWord(l)}</dd></div>
                <div><dt>{REF.rows.date}</dt><dd>{dayMonthYear(l.introduced_at)}</dd></div>
                {l.status === 'paid' && <div><dt>{REF.rows.reference}</dt><dd>{l.payment_reference || REF.none}</dd></div>}
                <div><dt>{REF.rows.code}</dt><dd>{l.code ? <code className="rf-code">{l.code}</code> : REF.none}</dd></div>
              </dl>
              {l.notes && <p className="rf-note">{REF.note(l.notes)}</p>}
              {lineActions(l).length > 0 && <Actions l={l} products={withOffers} busy={busy} onAct={onAct} />}
              {confirmFor(l)}
            </li>
          )
        })}
      </ul>
    )
  } else {
    const style = { '--ad-cols': COLS }
    body = (
      <div className="ad-table rf-table" role="table" aria-label={REF.tableLabel}>
        <div className="ad-thead" role="rowgroup">
          <div className="ad-tr" role="row" style={style}>
            {['referred', 'referrer', 'product', 'status', 'amount', 'date', 'actions'].map(k => <span key={k} role="columnheader">{REF.columns[k]}</span>)}
          </div>
        </div>
        <div className="ad-tbody" role="rowgroup">
          {shown.map(l => {
            const flag = flagWords(l)
            const refund = refundWords(l)
            const after = confirmFor(l)
            return (
              <div key={l.id} id={`rf-line-${l.id}`} className={`rf-line${focusLine === l.id ? ' rf-line--picked' : ''}`} role="presentation">
                <div className="ad-tr" role="row" style={style}>
                  <span className="ad-td" role="cell">
                    <span className="ad-td-main rf-referred">{l.referred_name || REF.someone}</span>
                    {l.referred_email && <span className="ad-td-sub">{l.referred_email}</span>}
                  </span>
                  <span className="ad-td" role="cell"><Referrer l={l} onChange={line => setDialog({ kind: 'reassign', line })} /></span>
                  <span className="ad-td" role="cell">{productWord(l)}</span>
                  <span className="ad-td rf-status" role="cell"><Pill l={l} />{flag && <span className="rf-flag">{flag}</span>}{refund && <span className="rf-flag rf-refunded">{refund}</span>}</span>
                  <span className="ad-td" role="cell">{amountWord(l)}</span>
                  <span className="ad-td ad-td-quiet" role="cell">{dayMonthYear(l.introduced_at)}</span>
                  <span className="ad-td rf-acts-cell" role="cell"><Actions l={l} products={withOffers} busy={busy} onAct={onAct} /></span>
                </div>
                {(l.notes || after) && (
                  <div className="ad-tr-after rf-after" role="row">
                    <span role="cell">
                      {l.notes && <p className="rf-note">{REF.note(l.notes)}</p>}
                      {after}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <>
      {owed.length > 0 && (
        <Card title={REF.owedTitle} id="rf-owed" className="rf-owed">
          <ul className="rf-owed-list">
            {owed.map(o => (
              <li key={o.person_id}>
                <Link to={adminPath.person(o.person_id)}>{o.full_name}</Link>, {gbp(o.amount)}{o.refunded.length > 0 && '.'}
                {o.refunded.map(r => <span key={r.id} className="rf-owed-refunded"> {REF.owedRefunded(gbp(r.amount), dayMonthYear(r.at))}</span>)}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <div className="rf-filters">
        <Chips label={REF.statusLabel} className="rf-status-chips">
          {REF.statusChips.map(c => (
            <Chip key={c.key || 'all'} pressed={status === c.key} onClick={() => setStatus(c.key)}
              aria-label={c.key === 'needs_look' && looks > 0 ? `${c.label}, ${looks} to look at` : undefined}>
              {c.label}{c.key === 'needs_look' && looks > 0 && <span className="rf-count" aria-hidden="true">{looks}</span>}
            </Chip>
          ))}
        </Chips>
        <div className="rf-filter-row">
          <Field label={REF.productLabel} className="rf-product-filter">
            {props => <Select {...props} value={product} onChange={setProduct} placeholder={REF.allProducts}
              options={withOffers.map(p => ({ value: p.id, label: shortName(p) }))} />}
          </Field>
          <Button kind="ink" onClick={() => setDialog({ kind: 'add' })}>{REF.addByHand}</Button>
        </div>
      </div>
      {body}

      {dialog?.kind === 'qualify' && (
        <QualifyDialog line={dialog.line} offer={offerBy.get(dialog.line.product_id)} onClose={() => setDialog(null)} onDone={done} />
      )}
      {dialog?.kind === 'paid' && <PaidDialog line={dialog.line} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.kind === 'reassign' && <ReassignDialog line={dialog.line} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.kind === 'add' && <AddDialog products={withOffers} onClose={() => setDialog(null)} onDone={done} />}
    </>
  )
}
